import { useEffect, useId, useMemo, useState } from 'react';
import type { CandidateQueryProgress as CandidateQueryProgressData } from '@/entities/outreach-candidate';
import * as s from './CandidateCriteriaBar.css';

interface CandidateQueryProgressProps {
  state: 'queued' | 'running';
  queryId?: string | null;
  requestedAt?: string | null;
  progress?: CandidateQueryProgressData | null;
  progressUpdatedAt?: number;
}

interface ProgressObservation {
  attemptId: string;
  attempts: number;
  observedAt: number | null;
  phase: 'counting' | 'scanning' | 'finalizing' | null;
  totalUnits: number | null;
  completedUnits: number | null;
  sourceSnapshotAt: string | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function dateValue(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || !/(Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function safeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

function readObservation(
  state: CandidateQueryProgressProps['state'],
  queryId: string | null | undefined,
  progress: CandidateQueryProgressData | null | undefined
): ProgressObservation | null {
  if (
    !progress ||
    state !== 'running' ||
    progress.state !== state ||
    progress.queryId !== queryId ||
    progress.schemaVersion !== 1 ||
    progress.unit !== 'source_rows' ||
    !UUID.test(progress.queryId) ||
    !UUID.test(progress.attemptId ?? '') ||
    !safeInteger(progress.attempts) ||
    progress.attempts < 1
  )
    return null;

  const rawObservedAt = dateValue(progress.observedAt);
  const receivedAt = dateValue(progress.receivedAt);
  const observedAt =
    rawObservedAt !== null && receivedAt !== null && rawObservedAt <= receivedAt
      ? rawObservedAt
      : null;
  const snapshotAt = dateValue(progress.sourceSnapshotAt);
  const hasCounts =
    progress.measurement === 'measured' &&
    progress.reason === null &&
    safeInteger(progress.totalUnits) &&
    safeInteger(progress.completedUnits) &&
    progress.totalUnits > 0 &&
    progress.completedUnits >= 0 &&
    progress.completedUnits <= progress.totalUnits &&
    snapshotAt !== null &&
    observedAt !== null &&
    snapshotAt <= observedAt;
  const phase =
    hasCounts && progress.phase === 'scanning'
      ? 'scanning'
      : hasCounts &&
          progress.phase === 'finalizing' &&
          progress.completedUnits === progress.totalUnits
        ? 'finalizing'
        : progress.phase === 'counting' &&
            progress.measurement === 'unknown' &&
            progress.reason === 'denominator_pending' &&
            progress.totalUnits === null &&
            progress.completedUnits === null &&
            progress.sourceSnapshotAt === null
          ? 'counting'
          : null;
  return {
    attemptId: progress.attemptId as string,
    attempts: progress.attempts,
    observedAt,
    phase,
    totalUnits: hasCounts ? progress.totalUnits : null,
    completedUnits: hasCounts ? progress.completedUnits : null,
    sourceSnapshotAt: hasCounts ? progress.sourceSnapshotAt : null,
  };
}

function isFresh(
  progress: CandidateQueryProgressData | null | undefined,
  progressUpdatedAt: number | undefined,
  now: number
): boolean {
  if (
    !progress ||
    !safeInteger(progress.staleAfterSeconds) ||
    progress.staleAfterSeconds <= 0 ||
    typeof progressUpdatedAt !== 'number' ||
    !Number.isFinite(progressUpdatedAt) ||
    progressUpdatedAt < 0 ||
    progressUpdatedAt > now
  )
    return false;
  const observedAt = dateValue(progress.observedAt);
  const receivedAt = dateValue(progress.receivedAt);
  // 서버 관측 나이에 로컬 수신 이후 시간만 더한다. 재마운트나 브라우저 시계 차이가 TTL을 늘리지 않는다.
  return (
    observedAt !== null &&
    receivedAt !== null &&
    receivedAt >= observedAt &&
    receivedAt - observedAt + now - progressUpdatedAt <= progress.staleAfterSeconds * 1000
  );
}

function isRetrograde(previous: ProgressObservation | null, next: ProgressObservation): boolean {
  if (!previous) return false;
  if (next.attempts < previous.attempts) return true;
  if (next.attempts > previous.attempts) return next.attemptId === previous.attemptId;
  if (next.attemptId !== previous.attemptId) return true;
  if (
    next.observedAt !== null &&
    previous.observedAt !== null &&
    next.observedAt < previous.observedAt
  )
    return true;
  if (
    next.phase === 'counting' &&
    (previous.phase === 'scanning' || previous.phase === 'finalizing')
  )
    return true;
  if (next.phase === 'scanning' && previous.phase === 'finalizing') return true;
  if (next.totalUnits === null) return false;
  return (
    (previous.totalUnits !== null && previous.totalUnits !== next.totalUnits) ||
    (previous.sourceSnapshotAt !== null && previous.sourceSnapshotAt !== next.sourceSnapshotAt) ||
    (previous.completedUnits !== null &&
      next.completedUnits !== null &&
      next.completedUnits < previous.completedUnits)
  );
}

function advanceObservation(
  previous: ProgressObservation | null,
  next: ProgressObservation
): ProgressObservation {
  if (!previous || (next.attempts > previous.attempts && !isRetrograde(previous, next)))
    return next;
  if (isRetrograde(previous, next)) return previous;
  const merged = {
    ...next,
    observedAt: next.observedAt ?? previous.observedAt,
    phase: next.phase ?? previous.phase,
    totalUnits: next.totalUnits ?? previous.totalUnits,
    completedUnits: next.completedUnits ?? previous.completedUnits,
    sourceSnapshotAt: next.sourceSnapshotAt ?? previous.sourceSnapshotAt,
  };
  return (Object.keys(merged) as (keyof ProgressObservation)[]).every(
    (key) => merged[key] === previous[key]
  )
    ? previous
    : merged;
}

export function CandidateQueryProgress({
  state,
  queryId,
  requestedAt,
  progress,
  progressUpdatedAt,
}: CandidateQueryProgressProps) {
  const [now, setNow] = useState(Date.now);
  const [history, setHistory] = useState<{
    input: ProgressObservation | null;
    value: ProgressObservation | null;
  }>({ input: null, value: null });
  const highWater = history.value;
  const statusId = useId();
  const startedAt = requestedAt ? Date.parse(requestedAt) : NaN;
  const hasTime = Number.isFinite(startedAt);
  const seconds = hasTime ? Math.max(0, Math.floor((now - startedAt) / 1000)) : null;
  const observation = useMemo(
    () => readObservation(state, queryId, progress),
    [state, queryId, progress]
  );
  const accepted =
    observation &&
    !isRetrograde(highWater, observation) &&
    isFresh(progress, progressUpdatedAt, now);
  const measured =
    accepted &&
    observation.phase === 'scanning' &&
    observation.completedUnits !== null &&
    observation.totalUnits !== null
      ? { completedUnits: observation.completedUnits, totalUnits: observation.totalUnits }
      : null;
  const percentage = measured ? (measured.completedUnits / measured.totalUnits) * 100 : null;
  const labelPercentage = measured
    ? Math.floor((measured.completedUnits / measured.totalUnits) * 1000) / 10
    : null;
  const knownPhase = accepted ? observation.phase : null;

  if (history.input !== observation) {
    setHistory({
      input: observation,
      value: observation ? advanceObservation(highWater, observation) : highWater,
    });
  }

  useEffect(() => {
    if (!hasTime && !progress) return;
    const tick = () => setNow(Date.now());
    const refresh = window.setTimeout(tick, 0);
    const timer = window.setInterval(tick, 1000);
    return () => {
      window.clearTimeout(refresh);
      window.clearInterval(timer);
    };
  }, [hasTime, progress, progressUpdatedAt]);

  const status =
    state === 'queued'
      ? '후보 조회를 기다리고 있습니다.'
      : percentage !== null
        ? `전체 데이터 확인 ${labelPercentage?.toFixed(1)}% · 후보를 조회하고 있습니다.`
        : knownPhase === 'counting'
          ? '조회할 데이터를 확인하고 있습니다. 진행률은 확인 후 표시됩니다.'
          : knownPhase === 'finalizing'
            ? '데이터 확인을 마쳤습니다. 조회 결과를 정리하고 있습니다.'
            : '후보를 조회하고 있습니다.';
  const specific = percentage !== null || knownPhase === 'counting' || knownPhase === 'finalizing';

  return (
    <div className={s.queryProgress}>
      <p id={statusId} className={s.progress} role="status" aria-atomic="true">
        {specific ? status : `${status} 걸리는 시간은 조건과 서버 상황에 따라 달라집니다.`}
      </p>
      <div
        className={s.progressTrack}
        role="progressbar"
        aria-label={status}
        aria-describedby={statusId}
        aria-valuemin={percentage !== null ? 0 : undefined}
        aria-valuemax={percentage !== null ? 100 : undefined}
        aria-valuenow={percentage ?? undefined}
      >
        <span
          className={percentage !== null ? s.progressSegmentMeasured : s.progressSegment}
          style={percentage !== null ? { width: `${percentage}%` } : undefined}
        />
      </div>
      <p className={s.elapsed} aria-live="off">
        {seconds === null
          ? '경과 시간을 확인할 수 없습니다.'
          : `요청 후 ${Math.floor(seconds / 60)}분 ${String(seconds % 60).padStart(2, '0')}초가 지났습니다.`}
      </p>
      <p className={s.progress}>기다리는 동안 조건을 고치거나 다른 나라로 옮겨도 됩니다.</p>
    </div>
  );
}
